/**
 * Shared client-side Langfuse tracing primitives.
 *
 * The Excel and PowerPoint add-in pipelines each expose a thin, host-typed
 * wrapper (`excel/langfuseTrace.ts`, `powerpoint/langfuseTrace.ts`) around the
 * helpers here so the metadata header name, run-id scheme, encoding, and
 * outcome-posting transport live in one place instead of drifting between
 * hosts. The embeddings service reuses the header + encoder directly.
 */
import { buildApiUrl } from "../config";
import { withCredentials } from "../apiClient";
import type { SkillOrigin } from "../skills/types";

declare const __LANGFUSE_ENABLED__: boolean;

export const LANGFUSE_METADATA_HEADER = "X-Finster-Langfuse-Metadata";
export const LANGFUSE_SCHEMA_VERSION = "1";
const LANGFUSE_RUN_START_TIMEOUT_MS = 1_000;

/**
 * A Skill the run applied, as the server stamps it on the trace for the UBS
 * usage log (`skill.ids`, `skill.names`, `skill.source`). Langfuse only: never
 * part of the Amplitude Skill summary.
 */
export interface LangfuseRunSkill {
  id: string;
  name: string;
  source: "personal" | "org" | "finster";
}

export interface OfficeLangfuseRunContext {
  runId: string;
  requestId: string;
  correlationId: string;
  sessionId?: string;
  host: "excel" | "powerpoint";
  surface: string;
  mode: string;
  hasContext: boolean;
  traceId?: string;
  rootSpanId?: string;
  traceStartedAt?: string;
  traceInput?: unknown;
}

const SOURCE_BY_ORIGIN: Record<SkillOrigin, LangfuseRunSkill["source"]> = {
  personal: "personal",
  organization: "org",
  finster: "finster",
};

/** Maps the run's resolved Skills to what the trace records about them. */
export function toLangfuseRunSkills(
  skills: readonly { id: string; name: string; origin: SkillOrigin }[],
): LangfuseRunSkill[] {
  return skills.map((skill) => ({
    id: skill.id,
    name: skill.name,
    source: SOURCE_BY_ORIGIN[skill.origin],
  }));
}

function isClientLangfuseEnabled(): boolean {
  return typeof __LANGFUSE_ENABLED__ === "boolean" && __LANGFUSE_ENABLED__;
}

/**
 * Generate a prefixed run id (e.g. `excel-<uuid>` / `pptx-<uuid>`), falling
 * back to a time + random id when `crypto.randomUUID` is unavailable.
 */
export function createLangfuseRunId(prefix: string): string {
  const cryptoLike = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoLike?.randomUUID === "function") {
    return `${prefix}-${cryptoLike.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function encodeLangfuseMetadataHeader(metadata: Record<string, unknown>): string {
  return encodeURIComponent(JSON.stringify(metadata));
}

/**
 * Attach the Langfuse metadata header to a request init. Returns `init`
 * unchanged when there is no metadata so out-of-scope calls stay untraced.
 */
export function withLangfuseMetadataHeader(
  init: RequestInit,
  metadata: Record<string, unknown> | undefined,
): RequestInit {
  if (!metadata) return init;
  const headers = new Headers(init.headers || {});
  headers.set(LANGFUSE_METADATA_HEADER, encodeLangfuseMetadataHeader(metadata));
  return { ...init, headers };
}

/**
 * Build run metadata from a base object (the run context, or a host/surface
 * fallback), the per-host trace name, and any per-request overrides.
 */
export function buildLangfuseMetadata(
  base: object,
  traceName: string,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  const publicBase = { ...(base as Record<string, unknown>) };
  const addinMode = publicBase.mode;
  const addinSurface = publicBase.surface;
  delete publicBase.mode;
  delete publicBase.surface;
  delete publicBase.traceInput;
  delete publicBase.traceStartedAt;
  delete publicBase.traceId;
  delete publicBase.rootSpanId;
  return {
    ...publicBase,
    ...(addinMode !== undefined ? { ui_mode: addinMode } : {}),
    ...(addinSurface !== undefined ? { addin_surface: addinSurface } : {}),
    schema_version: LANGFUSE_SCHEMA_VERSION,
    trigger: "interactive",
    traceName,
    ...overrides,
  };
}

/**
 * Starts the native server-side AGENT for one submitted Office turn.
 *
 * The returned W3C identifiers are the identifiers generated by OpenTelemetry,
 * not client-generated placeholders. They are copied onto the mutable run
 * context so all later provider/tool requests can attach to the same root.
 * Tracing failure is deliberately non-fatal to the user workflow.
 */
export async function startLangfuseRun(params: {
  endpoint: string;
  context: OfficeLangfuseRunContext;
  metadata: Record<string, unknown>;
  input: unknown;
  hostLabel: string;
}): Promise<void> {
  if (!isClientLangfuseEnabled()) return;
  params.context.traceStartedAt = new Date().toISOString();
  params.context.traceInput = params.input;
  const abortController = new AbortController();
  const timeout = globalThis.setTimeout(
    () => abortController.abort(),
    LANGFUSE_RUN_START_TIMEOUT_MS,
  );
  try {
    const response = await fetch(
      buildApiUrl(params.endpoint),
      withLangfuseMetadataHeader(
        withCredentials({
          method: "POST",
          body: JSON.stringify({ input: params.input }),
          signal: abortController.signal,
        }),
        params.metadata,
      ),
    );
    // A disabled or unavailable server-side tracer intentionally returns 204.
    // Treat that as a clean no-op rather than attempting to parse an empty body.
    if (response.status === 204) return;
    if (!response.ok) {
      console.warn(`[Langfuse] ${params.hostLabel} run start returned non-2xx`, {
        status: response.status,
      });
      return;
    }
    const body = (await response.json()) as { traceId?: string; rootSpanId?: string };
    if (/^[a-f0-9]{32}$/.test(body.traceId || "") && /^[a-f0-9]{16}$/.test(body.rootSpanId || "")) {
      params.context.traceId = body.traceId;
      params.context.rootSpanId = body.rootSpanId;
    }
  } catch (error) {
    if (abortController.signal.aborted) return;
    console.warn(`[Langfuse] Failed to start ${params.hostLabel} run`, error);
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

/**
 * Fire-and-forget POST of a run outcome. Errors are swallowed (logged only) so
 * observability never surfaces to the user.
 */
export function postLangfuseOutcome(params: {
  endpoint: string;
  metadata: Record<string, unknown>;
  outcome: unknown;
  input?: unknown;
  startTime?: string;
  hostLabel: string;
}): void {
  if (!isClientLangfuseEnabled()) return;

  void fetch(
    buildApiUrl(params.endpoint),
    withLangfuseMetadataHeader(
      withCredentials({
        method: "POST",
        body: JSON.stringify({
          outcome: params.outcome,
          input: params.input,
          startTime: params.startTime,
        }),
        keepalive: true,
      }),
      params.metadata,
    ),
  )
    .then((response) => {
      if (!response.ok) {
        console.warn(`[Langfuse] ${params.hostLabel} outcome endpoint returned non-2xx`, {
          status: response.status,
        });
      }
    })
    .catch((error) => {
      console.warn(`[Langfuse] Failed to post ${params.hostLabel} outcome`, error);
    });
}

export interface LangfusePostedObservation {
  name: string;
  spanKey?: string;
  input?: unknown;
  output?: unknown;
  metadata?: Record<string, unknown>;
  level?: "DEFAULT" | "ERROR";
  statusMessage?: string;
  startTime?: string;
  endTime?: string;
}

/**
 * Fire-and-forget POST of a generic observation payload. Errors are swallowed
 * (logged only) so observability never impacts user-facing behavior.
 */
export function postLangfuseObservation(params: {
  endpoint: string;
  metadata: Record<string, unknown>;
  observation: LangfusePostedObservation;
  hostLabel: string;
}): void {
  if (!isClientLangfuseEnabled()) return;
  if (!params.observation?.name?.trim()) return;

  void fetch(
    buildApiUrl(params.endpoint),
    withLangfuseMetadataHeader(
      withCredentials({
        method: "POST",
        body: JSON.stringify({ observation: params.observation }),
      }),
      params.metadata,
    ),
  )
    .then((response) => {
      if (!response.ok) {
        console.warn(`[Langfuse] ${params.hostLabel} observation endpoint returned non-2xx`, {
          status: response.status,
        });
      }
    })
    .catch((error) => {
      console.warn(`[Langfuse] Failed to post ${params.hostLabel} observation`, error);
    });
}
